/**
 * Formatos estruturados na borda da interface.
 *
 * Estas funções só vestem ou traduzem o texto digitado. O valor que segue para a API
 * continua sendo montado pelos formulários no formato canônico de cada contrato.
 */
export function somenteDigitos(bruto: string, limite?: number): string {
  const digitos = bruto.replace(/\D/g, '')
  return limite === undefined ? digitos : digitos.slice(0, limite)
}

/** CPF até 11 dígitos; CNPJ a partir do 12º. */
export function mascararDocumentoBr(bruto: string): string {
  const d = somenteDigitos(bruto, 14)
  if (d.length <= 11) {
    return d
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3}\.\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3}\.\d{3}\.\d{3})(\d{1,2})$/, '$1-$2')
  }
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2}\.\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{2}\.\d{3}\.\d{3})(\d)/, '$1/$2')
    .replace(/^(\d{2}\.\d{3}\.\d{3}\/\d{4})(\d{1,2})$/, '$1-$2')
}

export function mascararCpfBr(bruto: string): string {
  return mascararDocumentoBr(somenteDigitos(bruto, 11))
}

/**
 * Documento para LEITURA, inteiro e com a pontuação do país (item 16 do lote coletado em
 * 24/09/2026 · D-149 · DS-560; GLOSSARIO, Formatação).
 *
 * O fundador decidiu que o CPF e o CNPJ aparecem inteiros para todo usuário interno. Esta é
 * a função ÚNICA da tela para isso: a ficha da 3.4, a 6.6 e as três sublinhas de fornecedor,
 * a 3.2 e a ficha do RH leem daqui, e nenhuma escreve a própria pontuação.
 *
 *  - sem documento (nulo, indefinido ou vazio): o marcador de célula vazia;
 *  - fora do Brasil: como o país o escreve, sem mexer;
 *  - no Brasil, limpo do que não é letra nem dígito e em maiúsculas: com 11 dígitos,
 *    `000.000.000-00`; com 14 caracteres, `00.000.000/0000-00` (o CNPJ alfanumérico
 *    incluído, D-66); qualquer outro, como veio.
 *
 * Aplicada a um valor já pontuado, devolve o mesmo valor.
 */
export function documentoParaLeitura(documento: string | null | undefined, pais = 'BR'): string {
  if (documento === null || documento === undefined || documento === '') return '—'
  if (pais !== 'BR') return documento
  const limpo = documento.replace(/[^0-9A-Za-z]/g, '').toUpperCase()
  if (/^\d{11}$/.test(limpo)) {
    return `${limpo.slice(0, 3)}.${limpo.slice(3, 6)}.${limpo.slice(6, 9)}-${limpo.slice(9)}`
  }
  if (/^[0-9A-Z]{14}$/.test(limpo)) {
    return `${limpo.slice(0, 2)}.${limpo.slice(2, 5)}.${limpo.slice(5, 8)}/${limpo.slice(8, 12)}-${limpo.slice(12)}`
  }
  return documento
}

/** Telefone brasileiro; números iniciados por `+` preservam a edição internacional. */
export function mascararTelefoneBr(bruto: string): string {
  if (bruto.trimStart().startsWith('+')) {
    const limpo = bruto.trimStart().replace(/[^+\d\s().-]/g, '')
    return `+${limpo.slice(1).replace(/\+/g, '')}`
  }
  const digitos = somenteDigitos(bruto, 11)
  if (digitos.length <= 2) return digitos
  const ddd = digitos.slice(0, 2)
  const resto = digitos.slice(2)
  if (resto.length <= 4) return `(${ddd}) ${resto}`
  const corte = resto.length > 8 ? 5 : 4
  return `(${ddd}) ${resto.slice(0, corte)}-${resto.slice(corte)}`
}

export function mascararCepBr(bruto: string): string {
  const digitos = somenteDigitos(bruto, 8)
  return digitos.length <= 5 ? digitos : `${digitos.slice(0, 5)}-${digitos.slice(5)}`
}

/**
 * Duração falada para horas decimais do contrato.
 * Aceita horas, hora+minutos, minutos isolados e decimal com vírgula ou ponto.
 */
export function duracaoParaDecimal(texto: string): string | null {
  const limpo = texto.trim().toLocaleLowerCase('pt-BR').replace(',', '.')
  const soMinutos = /^(\d{1,4})\s*(?:min|m)$/.exec(limpo)
  if (soMinutos !== null) {
    const minutos = Number(soMinutos[1])
    return minutos > 59 ? null : (minutos / 60).toFixed(2)
  }
  const comMinutos = /^(\d{1,3})\s*(?:h|:)\s*(\d{1,2})(?:\s*(?:min|m))?$/.exec(limpo)
  if (comMinutos !== null) {
    const horas = Number(comMinutos[1])
    const minutos = Number(comMinutos[2])
    return minutos > 59 ? null : (horas + minutos / 60).toFixed(2)
  }
  const soHoras = /^(\d{1,3}(?:\.\d{1,2})?)\s*h?$/.exec(limpo)
  return soHoras === null ? null : Number(soHoras[1]).toFixed(2)
}

export function duracaoDoDecimal(decimal: string): string {
  const horas = Number(decimal)
  if (!Number.isFinite(horas)) return decimal
  const minutosTotais = Math.round(horas * 60)
  const inteiras = Math.floor(minutosTotais / 60)
  const minutos = minutosTotais % 60
  if (inteiras === 0) return `${String(minutos)}min`
  return minutos === 0 ? `${String(inteiras)}h` : `${String(inteiras)}h${String(minutos).padStart(2, '0')}`
}

export function normalizarDuracaoParaEdicao(texto: string): string {
  const canonico = duracaoParaDecimal(texto)
  return canonico === null ? texto : duracaoDoDecimal(canonico)
}
